import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Building2, Download, Plus, Search, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import { ROUTES } from '@/constants';
import { APP } from '@/config';
import { useDebounce } from '@/hooks';
import { useCustomers } from '@/services/queries';
import { customersService } from '@/services/api/customers.service';
import { datedFilename, downloadCsv, toCsv } from '@/lib/csv';
import type { CustomerListQuery, CustomerSummary } from '@/types';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { StatusPill } from '@/shared/ui/status-pill';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { EmptyState } from '@/components/common';
import { cn } from '@/lib/utils';
import { CreateOrganizationDialog } from './CreateOrganizationDialog';

const PAGE_SIZE = 10;

const PAGE_WINDOW = 3;

function pageWindow(current: number, totalPages: number, size = PAGE_WINDOW): number[] {
  if (totalPages <= 0) return [];

  let start = Math.max(1, current - 1);
  let end = start + size - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - size + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/** "14 Mar 2027", or a dash. Never a raw ISO string. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const STATE_LABEL: Record<string, string> = {
  active: 'Active',
  expiring_soon: 'Expiring',
  expired: 'Expired',
  not_activated: 'Not activated',
};

const STATE_STATUS: Record<string, string> = {
  active: 'active',
  expiring_soon: 'expiring',
  expired: 'expired',
  not_activated: 'unassigned',
};

const ACTION =
  'inline-flex h-9 items-center gap-1.75 rounded-lg px-3.75 text-[12.5px] font-semibold whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-60';
const ACTION_PRIMARY = cn(ACTION, 'bg-primary text-primary-foreground hover:bg-brand-500');
const ACTION_OUTLINE = cn(
  ACTION,
  'border border-input bg-card text-foreground hover:border-primary',
);

/** Columns of the export, in the order the table shows them. */
const CSV_HEADER = [
  'Organisation',
  'Type',
  'Contact',
  'Email',
  'Licences',
  'Seats bound',
  'Seats total',
  'Expires',
  'Status',
];

function csvRow(customer: CustomerSummary): (string | number)[] {
  const licence = customer.licence;
  return [
    customer.name,
    customer.orgType === 'lab' ? 'Lab' : 'Dentist',
    customer.primaryContactName ?? '',
    customer.primaryEmail,
    customer.licenceCount,
    licence?.seatsBound ?? '',
    licence?.seatCount ?? '',
    licence?.expiresAt?.slice(0, 10) ?? '',
    licence ? (STATE_LABEL[licence.state] ?? licence.state) : 'No licence',
  ];
}

/**
 * The org-type chip: Lab or Dentist.
 *
 * Every value here is taken verbatim from the design canvas's own seat-type
 * chip ("Lab Seat" / "Single Seat") in the Users & Licenses table -
 * `typeBg`/`typeFg` in its row-mapping code, and the inline `<span style>`
 * that renders them:
 *
 *   display:inline-flex; align-items:center; justify-content:center;
 *   gap:6px; min-width:88px; text-align:center; font-size:11.5px;
 *   font-weight:600; color:{typeFg}; background:{typeBg};
 *   padding:3px 9px; border-radius:5px
 *
 *   typeBg: 'Lab Seat' -> #F1F5F9 (= --color-info-50)   else #F1F4F8 (= --color-neutral-100)
 *   typeFg: 'Lab Seat' -> #5b7ef0 (= --color-brand-500) else #5B6B87 (no matching token)
 *
 * The two backgrounds differ by one colour channel and read as identical at a
 * glance, which is why this looked like one shared fill from a screenshot -
 * it is not; each type keeps its own pair, matching the source rather than
 * an eyeballed approximation of it. No border in the source, so none here.
 */
function OrgTypeBadge({ orgType }: { orgType: CustomerSummary['orgType'] }) {
  const isLab = orgType === 'lab';
  return (
    <span
      className={cn(
        'inline-flex min-w-22 items-center justify-center gap-1.5 rounded-[5px] px-2.25 py-0.75 text-center text-[11.5px] font-semibold whitespace-nowrap',
        isLab ? 'bg-info-50 text-brand-500' : 'bg-neutral-100 text-[#5B6B87]',
      )}
    >
      {isLab ? 'Lab' : 'Dentist'}
    </span>
  );
}

function LicenceCells({ licence }: { licence: CustomerSummary['licence'] }) {
  if (!licence) {
    return (
      <>
        <TableCell className="font-mono text-muted-foreground">—</TableCell>
        <TableCell>
          <StatusPill status="unassigned" minWidth={92}>
            No licence
          </StatusPill>
        </TableCell>
      </>
    );
  }

  return (
    <>
      <TableCell className="font-mono text-muted-foreground">
        {/* Bound over bought - "3 / 5" is the number worth scanning a column of. */}
        {licence.seatsBound} / {licence.seatCount}
      </TableCell>
      <TableCell>
        <StatusPill status={STATE_STATUS[licence.state] ?? licence.state} minWidth={92}>
          {STATE_LABEL[licence.state] ?? licence.state}
        </StatusPill>
      </TableCell>
    </>
  );
}

/** `'all'` is a UI sentinel, not a server value - Radix Select refuses an
 *  empty-string item, and `undefined` is what the query actually wants when
 *  nothing is chosen. */
type OrgTypeFilter = NonNullable<CustomerListQuery['orgType']> | 'all';
type StatusFilterValue = NonNullable<CustomerListQuery['status']> | 'all';

export function CustomerTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [orgTypeFilter, setOrgTypeFilter] = useState<OrgTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [offset, setOffset] = useState(0);

  const [open, setOpen] = useState(location.pathname === ROUTES.newCustomer);

  const closeDialog = (next: boolean) => {
    setOpen(next);
    if (!next && location.pathname === ROUTES.newCustomer) {
      void navigate(ROUTES.customers, { replace: true });
    }
  };

  const debounced = useDebounce(search, APP.ui.debounceMs);
  const term = debounced.trim();

  /** The filters that go on every request against this list - search, and
   *  now the two dropdowns - built once so the query and the CSV export read
   *  off the same thing rather than assembling it twice and risking one of
   *  them drifting from what the screen is actually showing. */
  const filters: Omit<CustomerListQuery, 'limit' | 'offset'> = {
    ...(term ? { search: term } : {}),
    ...(orgTypeFilter !== 'all' ? { orgType: orgTypeFilter } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
  };

  const query = useCustomers({ limit: PAGE_SIZE, offset, ...filters });

  const onSearch = (value: string) => {
    setSearch(value);
    // Page 3 of the old results is meaningless against new ones.
    setOffset(0);
  };

  const onOrgTypeFilterChange = (value: string) => {
    setOrgTypeFilter(value as OrgTypeFilter);
    setOffset(0);
  };

  const onStatusFilterChange = (value: string) => {
    setStatusFilter(value as StatusFilterValue);
    setOffset(0);
  };

  const rows = query.data?.data;
  const isEmpty = rows !== undefined && rows.length === 0;

  const [exporting, setExporting] = useState(false);

  const exportCsv = () => {
    setExporting(true);
    customersService
      .listAll(filters)
      .then((all) => {
        downloadCsv(datedFilename('customers'), toCsv(CSV_HEADER, all.map(csvRow)));
        toast.success(
          `Exported ${String(all.length)} ${all.length === 1 ? 'customer' : 'customers'}.`,
        );
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : 'Could not build the export.');
      })
      .finally(() => {
        setExporting(false);
      });
  };

  const newOrganisation = (
    <button
      type="button"
      onClick={() => {
        setOpen(true);
      }}
      className={ACTION_PRIMARY}
    >
      <Plus size={13} strokeWidth={2.4} aria-hidden="true" />
      New organisation
    </button>
  );

  return (
    <div className="space-y-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-60 flex-1 sm:max-w-85">
          <Search
            size={15}
            strokeWidth={2}
            className="absolute top-1/2 left-2.75 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            className="h-9 w-full rounded-lg border border-input bg-card pr-3 pl-8.25 text-[12.5px] outline-none placeholder:text-muted-foreground focus-visible:border-ring"
            placeholder="Search name, contact or email"
            value={search}
            onChange={(event) => {
              onSearch(event.target.value);
            }}
            aria-label="Search customers"
          />
        </div>

        <Select value={orgTypeFilter} onValueChange={onOrgTypeFilterChange}>
          <SelectTrigger
            size="sm"
            className="w-36 text-[12.5px] bg-white"
            aria-label="Filter by type"
          >
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="lab">Lab</SelectItem>
            <SelectItem value="individual_dentist">Dentist</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger
            size="sm"
            className="w-40 text-[12.5px] bg-white"
            aria-label="Filter by status"
          >
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="no_licence">No licence</SelectItem>
            <SelectItem value="not_activated">Not activated</SelectItem>
            <SelectItem value="active">Active</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <button
          type="button"
          onClick={exportCsv}
          // Nothing to export is a real state on this screen, and a file with
          // a header row and no rows in it is not a useful answer to it.
          disabled={exporting || isEmpty}
          className={ACTION_OUTLINE}
        >
          <Download size={13} strokeWidth={2} aria-hidden="true" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>

        {newOrganisation}
      </div>

      <CreateOrganizationDialog open={open} onOpenChange={closeDialog} />

      {/* {query.isError && (
        <ErrorState
          title="Could not load customers"
          description={query.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )} */}

      {/*
        An empty result replaces the table rather than sitting inside it.
        A column header over nothing is a promise of data that is not there,
        and the design draws this state as a block in the card's place - the
        point of it being to offer the one move worth making, which a grey
        sentence wedged into a table row cannot do.
      */}
      {isEmpty ? (
        <TableCard>
          {term ? (
            <EmptyState
              icon={SearchX}
              title="No customers match this search"
              description={`Nothing matches “${term}”. Check the spelling, or clear the search to see every organisation.`}
              actions={
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onSearch('');
                    }}
                    className={ACTION_PRIMARY}
                  >
                    Clear search
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(true);
                    }}
                    className={ACTION_OUTLINE}
                  >
                    <Plus size={13} strokeWidth={2.4} aria-hidden="true" />
                    New organisation
                  </button>
                </>
              }
            />
          ) : (
            <EmptyState
              icon={Building2}
              title="No organizations yet"
              description="Create an organization to get started. Its licence is issued separately, from the organization itself, once the deal is ready."
              actions={newOrganisation}
            />
          )}
        </TableCard>
      ) : (
        <TableCard>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[6%]">S.No.</TableHead>
                <TableHead className="w-[8%]">Type</TableHead>
                <TableHead className="w-[15%]">Organisation</TableHead>
                <TableHead className="w-[15%]">Contact</TableHead>
                <TableHead className="w-[17%]">Email</TableHead>
                <TableHead className="w-[10%]">Enrolled</TableHead>
                <TableHead className="w-[8%]">Plans</TableHead>
                <TableHead className="w-[8%]">Seats</TableHead>
                <TableHead className="w-[8%]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending &&
                Array.from({ length: 6 }, (_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={9}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}

              {rows?.map((customer, index) => (
                /*
                The whole row navigates - an admin triages many rows in
                sequence, and aiming at a link in one column is friction forty
                times over. The name is plain text, not a link of its own.
              */
                <TableRow
                  key={customer.id}
                  className="cursor-pointer"
                  onClick={() => {
                    void navigate(ROUTES.customerDetail(customer.id));
                  }}
                >
                  <TableCell className="font-mono text-[11.5px] text-muted-foreground">
                    {String(offset + index + 1).padStart(3, '0')}
                  </TableCell>
                  <TableCell>
                    <OrgTypeBadge orgType={customer.orgType} />
                  </TableCell>
                  <TableCell className="text-[12.5px] font-semibold">{customer.name}</TableCell>
                  <TableCell className="text-[12.5px]">
                    {customer.primaryContactName ?? '—'}
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">
                    {customer.primaryEmail}
                  </TableCell>
                  <TableCell className="font-mono text-[11.5px] text-muted-foreground">
                    {formatDate(customer.createdAt)}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {customer.licenceCount}
                  </TableCell>
                  <LicenceCells licence={customer.licence} />
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Nothing to page through when everything already fits on one page. */}
          {query.data && query.data.meta.total > PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-[11.5px] text-muted-foreground">
              <span>
                {`Showing ${String(offset + 1)}–${String(offset + query.data.data.length)} of ${String(query.data.meta.total)} organizations`}
              </span>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => {
                    setOffset(Math.max(0, offset - PAGE_SIZE));
                  }}
                >
                  Previous
                </Button>

                {(() => {
                  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
                  const totalPages = Math.max(1, Math.ceil(query.data.meta.total / PAGE_SIZE));

                  return pageWindow(currentPage, totalPages).map((page) => (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="icon-sm"
                      aria-current={page === currentPage ? 'page' : undefined}
                      onClick={() => {
                        setOffset((page - 1) * PAGE_SIZE);
                      }}
                    >
                      {page}
                    </Button>
                  ));
                })()}

                <Button
                  variant="outline"
                  size="sm"
                  // hasMore comes from the server rather than being inferred
                  // from a full page, which would claim another page exists
                  // whenever the total is an exact multiple of the page size.
                  disabled={!query.data.meta.hasMore}
                  onClick={() => {
                    setOffset(offset + PAGE_SIZE);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TableCard>
      )}
    </div>
  );
}
